/**
 * Users page component - Display Users with Real API Data
 * Connected to backend API for real-time user management
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui';
import { useCreateUser, useDeleteUser, useUserStatistics } from '@/hooks';
import { userService } from '../services/user-service';
import { UserFilters } from '@/components/UserFilters';
import { UsersEmptyState, UsersLoadingSkeleton, UsersErrorState } from '@/components/empty-states/UserEmptyStates';
import type { UserCreateRequest, UserStatus, User, UserFilterOptions, UserRole } from '@/types';
import { Badge } from '@/components/ui/badge';


const formatStatusDisplay = (status: UserStatus) => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'locked':
      return 'Locked';
    default:
      return status;
  }
};

const formatRolesDisplay = (roles: UserRole[]) => {
  return roles.map(role => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'mod':
        return 'Moderator';
      case 'user':
        return 'User';
      case 'readonly':
        return 'Read Only';
      default:
        return role;
    }
  }).join(', ');
};

export function Users() {
  const [isCreating, setIsCreating] = useState(false);
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(undefined);
  const [pageHistory, setPageHistory] = useState<(string | undefined)[]>([undefined]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [filters, setFilters] = useState<Omit<UserFilterOptions, 'name'>>({});
  const itemsPerPage = 25;
  
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    roles: ['user'] as UserRole[],
    status: 'active' as UserStatus,
  });

  const [users, setUsers] = useState<any[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string|undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const { data: stats, isLoading: statsLoading, error: statsError } = useUserStatistics(filters);
  const createUserMutation = useCreateUser();
  const deleteUserMutation = useDeleteUser();

  React.useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await userService.getUsers({ limit: itemsPerPage, nextToken: currentPageToken }, filters);
        if (!cancelled) {
          setUsers(resp.items || []);
          setHasNextPage(resp.hasMore);
          setNextPageToken(resp.nextToken);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [currentPageToken, filters]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const createRequest: UserCreateRequest = {
        name: newUserData.name,
        email: newUserData.email,
        roles: newUserData.roles,
        status: newUserData.status,
      };

      await createUserMutation.mutateAsync(createRequest);
      setIsCreating(false);
      setNewUserData({ name: '', email: '', roles: ['user'], status: 'active' });
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await deleteUserMutation.mutateAsync(userId);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleEditUser = (userId: string) => {
    alert(`Edit functionality for user "${userId}" is not yet implemented.`);
  };

  const handleFiltersChange = (newFilters: UserFilterOptions) => {
    // Remove 'name' from filters if present
    const { name, ...rest } = newFilters;
    setFilters(rest);
    setCurrentPageToken(undefined);
    setPageHistory([undefined]);
    setCurrentPageIndex(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPageToken(undefined);
    setPageHistory([undefined]);
    setCurrentPageIndex(0);
  };

  const handlePreviousPage = () => {
    if (currentPageIndex > 0) {
      const prevIndex = currentPageIndex - 1;
      setCurrentPageIndex(prevIndex);
      setCurrentPageToken(pageHistory[prevIndex]);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage && nextPageToken) {
      const newIndex = currentPageIndex + 1;
      if (pageHistory.length <= newIndex) {
        setPageHistory([...pageHistory, nextPageToken]);
      }
      setCurrentPageIndex(newIndex);
      setCurrentPageToken(nextPageToken);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="content-header">
          <h1 className="content-title">User Management</h1>
          <p className="content-subtitle">View and manage all users in your platform</p>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="content-header">
          <h1 className="content-title">User Management</h1>
          <p className="content-subtitle">View and manage all users in your platform</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="text-center py-8">
            <p className="text-red-600">Error loading users</p>
            <p className="text-sm text-gray-500 mt-2">
              {error instanceof Error ? error.message : 'Unable to load user data'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="content-header flex-shrink-0">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="content-title">User Management</h1>
            <p className="content-subtitle">View and manage all users in your platform</p>
          </div>
          <button
            className="access-btn access-primary"
            onClick={() => setIsCreating(true)}
            disabled={createUserMutation.isPending}
          >
            {createUserMutation.isPending ? 'Creating...' : 'Add New User'}
          </button>
        </div>
      </div>

      <div className="stats-overview flex-shrink-0">
        <div className="stat-item">
          <div className={`stat-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.total || 0)}
          </div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-item">
          <div className={`stat-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.active || 0)}
          </div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-item">
          <div className={`stat-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.locked || 0)}
          </div>
          <div className="stat-label">Locked</div>
        </div>
      </div>

      <UserFilters
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {Object.values(filters).some(value => 
        value !== undefined && value !== null && value !== '' && 
        (Array.isArray(value) ? value.length > 0 : true)
      ) && (
        <div className="mb-4">
          <div className="text-sm text-blue-600 font-medium">
            Filters Active
            {(() => {
              const activeFilters = [];
              if (filters.email) activeFilters.push(`Email: "${filters.email}"`);
              if (filters.status) activeFilters.push(`Status: ${filters.status}`);
              if (filters.role) activeFilters.push(`Role: ${filters.role}`);
              
              return activeFilters.length > 0 ? ` (${activeFilters.join(', ')})` : '';
            })()}
          </div>
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <UsersLoadingSkeleton />
        ) : error ? (
          <UsersErrorState 
            onRetry={() => window.location.reload()}
            error={typeof error === 'string' ? error : 'Failed to load users'}
          />
        ) : users.length === 0 ? (
          <UsersEmptyState 
            hasFilters={Object.values(filters).some(value => 
              value !== undefined && value !== null && value !== '' && 
              (Array.isArray(value) ? value.length > 0 : true)
            )}
            onCreateUser={() => setIsCreating(true)}
            onClearFilters={handleClearFilters}
          />
        ) : (
          <div className="table-container">
            <div className="table-scroll-wrapper">
              <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Roles</th>
              <th>Status</th>
              <th>Last Modified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user: User, index: number) => (
              <tr key={user.id}>
                <td>{itemsPerPage * currentPageIndex + index + 1}</td>
                <td>{user.id}</td>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{formatRolesDisplay(user.roles)}</td>
                <td>
                  <Badge variant={user.status}>
                    {formatStatusDisplay(user.status)}
                  </Badge>
                </td>
                <td>{user.lastModifiedTimestamp}</td>
                <td>
                  <div className="row-actions">
                    <button
                      className="access-btn access-secondary"
                      onClick={() => handleEditUser(user.id)}
                      disabled={createUserMutation.isPending || deleteUserMutation.isPending}
                    >
                      Edit
                    </button>
                    <button
                      className="access-btn access-destructive"
                      onClick={() => handleDeleteUser(user.id)}
                      disabled={createUserMutation.isPending || deleteUserMutation.isPending}
                    >
                      {deleteUserMutation.isPending ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        
        <div className="table-pagination">
          <div className="pagination-info">
            Showing {users.length > 0 ? `${itemsPerPage * currentPageIndex + 1} to ${itemsPerPage * currentPageIndex + users.length}` : '0 to 0'} entries (page {currentPageIndex + 1})
          </div>
          <div className="pagination-controls">
            <button 
              className="access-btn access-secondary" 
              onClick={handlePreviousPage}
              disabled={currentPageIndex === 0}
            >
              Previous
            </button>
            <button 
              className="access-btn access-secondary"
              onClick={handleNextPage}
              disabled={!hasNextPage}
            >
              Next
            </button>
          </div>
        </div>
        </div>
        )}
      </div>

      {isCreating && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3 className="modal-title">Add New User</h3>
            <form onSubmit={handleCreateUser} className="modal-form">
              <div className="access-form">
                <div className="access-form-item">
                  <label className="access-label" htmlFor="createUserName">Name</label>
                  <input
                    id="createUserName"
                    type="text"
                    className="access-input"
                    value={newUserData.name}
                    onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                    placeholder="e.g., John Doe"
                    required
                  />
                </div>
                <div className="access-form-item">
                  <label className="access-label" htmlFor="createUserEmail">Email</label>
                  <input
                    id="createUserEmail"
                    type="email"
                    className="access-input"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    placeholder="john.doe@example.com"
                    required
                  />
                </div>
                <div className="access-form-item">
                  <label className="access-label" htmlFor="createUserStatus">Status</label>
                  <select
                    id="createUserStatus"
                    className="access-input"
                    value={newUserData.status}
                    onChange={(e) => setNewUserData({ ...newUserData, status: e.target.value as UserStatus })}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="locked">Locked</option>
                  </select>
                </div>
                <div className="access-form-item">
                  <label className="access-label" htmlFor="createUserRoles">Roles</label>
                  <select
                    id="createUserRoles"
                    className="access-input"
                    value={newUserData.roles[0]}
                    onChange={(e) => setNewUserData({ ...newUserData, roles: [e.target.value as UserRole] })}
                    required
                  >
                    <option value="user">User</option>
                    <option value="readonly">Read Only</option>
                    <option value="mod">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="submit"
                  className="access-btn access-primary"
                  disabled={createUserMutation.isPending}
                >
                  {createUserMutation.isPending ? 'Creating...' : 'Add User'}
                </button>
                <button 
                  type="button"
                  className="access-btn access-secondary"
                  onClick={() => {
                    setIsCreating(false);
                    setNewUserData({ name: '', email: '', roles: ['user'], status: 'active' });
                  }}
                  disabled={createUserMutation.isPending}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
